import { type ReactNode, useEffect } from 'react';

import {
  AudioRecorderProvider,
  ChunkUploadProvider,
  MicrophoneProvider,
  eventTrackerEnabledAtom,
  registerUploadWorker,
} from '@repo/videa-notes';
import { useSetAtom } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';

import { usePreferences } from '../../../react-query/usePreferences';

// Module-level flag to ensure SW registration only happens once
let hasSwRegistrationBeenAttempted = false;

interface VideaNotesAudioRecorderProviderProps {
  children: ReactNode;
}

/**
 * Wrapper component that provides AudioRecorderProvider with org/practice context
 * and registers the upload service worker.
 */
export const VideaNotesAudioRecorderProvider = ({
  children,
}: VideaNotesAudioRecorderProviderProps) => {
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  const { data: preferences } = usePreferences();
  const setEventTrackerEnabled = useSetAtom(eventTrackerEnabledAtom);

  // Enable the event tracker only when the user has Voice Notes (avoids app_initialized for everyone)
  useEffect(() => {
    const isEventTrackerEnabled =
      preferences?.isVideaNotesEnabled === true ||
      preferences?.enableVoiceNotesStandalone === true;
    setEventTrackerEnabled(isEventTrackerEnabled);
  }, [
    preferences?.isVideaNotesEnabled,
    preferences?.enableVoiceNotesStandalone,
    setEventTrackerEnabled,
  ]);

  // Register the upload service worker once on first mount
  useEffect(() => {
    if (hasSwRegistrationBeenAttempted) return;
    hasSwRegistrationBeenAttempted = true;

    registerUploadWorker().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[VideaNotes] Failed to register upload worker:', err);
    });
  }, []);

  return (
    <MicrophoneProvider>
      <ChunkUploadProvider>
        <AudioRecorderProvider
          organizationId={currentOrganizationId}
          practiceId={currentPracticeId}
        >
          {children}
        </AudioRecorderProvider>
      </ChunkUploadProvider>
    </MicrophoneProvider>
  );
};
